import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faSearch,
    faFilter,
    faRefresh,
    faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api/api';
import { Dialog, DialogBackdrop, Transition } from '@headlessui/react';
import _ from 'lodash';
import LoadingLogo from '../../components/Loading';
import ComboBox from '../../components/atoms/ComboBox';
import { Menu } from '@headlessui/react';
import Pagination from '../../components/Pagination';



const AdminPet = () => {
    // ------------------
    // STATE
    // ------------------
    const [pets, setPets] = useState([]);
    const [loading, setLoading] = useState(true);
    // Modal / CRUD
    const [editPet, setEditPet] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Filtering
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedBreed, setSelectedBreed] = useState('all');
    const [vaccinatedFilter, setVaccinatedFilter] = useState('all');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedAllergies, setSelectedAllergies] = useState([]);



    // Toggle for showing/hiding advanced filters
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [meta, setMeta] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
        from: 1,
        to: 1,
    });

    // Characteristics (pet_types, pet_breeds, etc.)
    const [characteristics, setCharacteristics] = useState({
        pet_types: [],
        pet_breeds: [],
        pet_allergies: [],
    });

    // ------------------
    // FETCH DATA
    // ------------------
    const fetchPets = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/pets', {
                params: {
                    search: searchTerm,
                    pet_type: selectedType !== 'all' ? selectedType : null,
                    pet_breed: selectedBreed !== 'all' ? selectedBreed : null,
                    vaccinated: vaccinatedFilter !== 'all' ? vaccinatedFilter : null,
                    allergies: selectedAllergies,
                    from_date: fromDate || null,
                    to_date: toDate || null,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    page: currentPage,
                },
            });
            setPets(response.data);
            setMeta(response.meta);
        } catch (error) {
            toast.error('Failed to fetch pets.');
        } finally {
            setLoading(false);
        }
    }, [
        searchTerm,
        selectedType,
        selectedBreed,
        vaccinatedFilter,
        fromDate,
        toDate,
        sortBy,
        sortOrder,
        currentPage,
    ]);

    const fetchCharacteristics = useCallback(async (petTypeId = null) => {
        try {
            const response = await api.get('/pets/characteristics', {
                params: {
                    pet_type_id: petTypeId || undefined,
                },
            });
            setCharacteristics({
                pet_types: response.data.pet_types,
                pet_breeds: response.data.pet_breeds,
                pet_allergies: response.data.pet_allergies,
                owners: response.data.owners,
            });
        } catch (error) {
            toast.error('Failed to load characteristics.');
        }
    }, []);

    // Debounced fetch function to limit calls while typing
    const debouncedFetchPets = useCallback(_.debounce(fetchPets, 500), [fetchPets]);

    // useEffect that calls the debounced function
    useEffect(() => {
        debouncedFetchPets();
        return () => debouncedFetchPets.cancel();
    }, [
        searchTerm,
        selectedType,
        selectedBreed,
        vaccinatedFilter,
        selectedAllergies,
        fromDate,
        toDate,
        sortBy,
        sortOrder,
        currentPage,
        debouncedFetchPets,
    ]);

    // Load characteristics on first mount
    useEffect(() => {
        fetchCharacteristics();
    }, [fetchCharacteristics]);

    // ------------------
    // CRUD Handlers
    // ------------------
    const handleAdd = () => {
        setEditPet(null);
        setIsModalOpen(true);
        fetchCharacteristics();
    };

    const handleEdit = (pet) => {
        setEditPet(pet);
        setIsModalOpen(true);
        fetchCharacteristics(pet.pet_type_id);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this pet?')) {
            try {
                await api.delete(`/pets/${id}`);
                toast.success('Pet deleted successfully.');
                fetchPets(); // Refetch
            } catch (error) {
                toast.error('Failed to delete pet.');
            }
        }
    };

    const handleSave = () => {
        setIsModalOpen(false);
        fetchPets();
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleResetFilters = () => {
        setSearchTerm('')
        setSelectedType('all')
        setSelectedBreed('all')
        setVaccinatedFilter('all')
        setSelectedAllergies([])
        setSortBy('created_at')
        setSortOrder('desc')
        setCurrentPage(1)
        setFromDate('')
        setToDate('')
    };

    const handleDownloadCSV = async () => {
        try {
            setLoading(true);

            const response = await api.get('/pets', {
                params: {
                    search: searchTerm,
                    pet_type: selectedType !== 'all' ? selectedType : null,
                    pet_breed: selectedBreed !== 'all' ? selectedBreed : null,
                    vaccinated: vaccinatedFilter !== 'all' ? vaccinatedFilter : null,
                    from_date: fromDate || null,
                    to_date: toDate || null,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    allergies: selectedAllergies,
                    want_csv: 1,
                },
                responseType: 'blob',
            });

            // ✅ Create a download link for the CSV
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'pets_export.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setLoading(false);

        } catch (error) {
            // Failed to download CSV
        }
    };


    // ------------------
    // PAGINATION
    // ------------------
    const handleNextPage = () => {
        if (meta.current_page < meta.last_page) {
            setCurrentPage(meta.current_page + 1);
        }
    };

    const handlePrevPage = () => {
        if (meta.current_page > 1) {
            setCurrentPage(meta.current_page - 1);
        }
    };



    const renderPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= meta.last_page; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`px-3 py-1 rounded-lg border border-tblue ${i === meta.current_page ? 'bg-tblue text-white' : 'bg-white text-gray-700'
                        } hover:bg-tblue hover:text-white mx-1`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    // ------------------
    // RENDER
    // ------------------
    if (loading) {
        return <LoadingLogo />;
    }

    return (
        <div className="mx-auto px-2 sm:px-4 py-2 sm:py-2">

            {/* Advanced Filters Section */}
            <div className={`
            overflow-hidden 
            transition-all 
            duration-300 
            ease-in-out 
            ${showAdvanced ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                    {/* Pet Type */}
                    <div className="flex flex-col">
                        <label htmlFor="type-filter" className="text-tblue font-medium mb-1">
                            Type
                        </label>
                        <select
                            id="type-filter"
                            value={selectedType}
                            onChange={(e) => {
                                setSelectedType(e.target.value);
                                setCurrentPage(1);
                                fetchCharacteristics(e.target.value); // Refresh breed list if needed
                            }}
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm 
                         focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                        >
                            <option value="all">All Types</option>
                            {characteristics.pet_types.map((type) => (
                                <option key={type.id} value={type.id}>
                                    {type.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Breed */}
                    <div className="flex flex-col">
                        <label htmlFor="breed-filter" className="text-tblue font-medium mb-1">
                            Breed
                        </label>
                        <select
                            id="breed-filter"
                            value={selectedBreed}
                            onChange={(e) => {
                                setSelectedBreed(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm 
                         focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                        >
                            <option value="all">All Breeds</option>
                            {characteristics.pet_breeds.map((breed) => (
                                <option key={breed.id} value={breed.id}>
                                    {breed.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Vaccinated Status */}
                    <div className="flex flex-col">
                        <label htmlFor="vaccinated-filter" className="text-tblue font-medium mb-1">
                            Vaccinated
                        </label>
                        <select
                            id="vaccinated-filter"
                            value={vaccinatedFilter}
                            onChange={(e) => {
                                setVaccinatedFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm 
                         focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                        >
                            <option value="all">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>

                    {/* Date Range: From */}
                    <div className="flex flex-col">
                        <label htmlFor="from-date" className="text-tblue font-medium mb-1">
                            From
                        </label>
                        <input
                            id="from-date"
                            type="date"
                            value={fromDate}
                            onChange={(e) => {
                                setFromDate(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm 
                         focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                        />
                    </div>

                    {/* Date Range: To */}
                    <div className="flex flex-col">
                        <label htmlFor="to-date" className="text-tblue font-medium mb-1">
                            To
                        </label>
                        <input
                            id="to-date"
                            type="date"
                            value={toDate}
                            onChange={(e) => {
                                setToDate(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm 
                         focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                        />
                    </div>

                    {/* Sorting: Sort By */}
                    <div className="flex flex-col">
                        <label htmlFor="sort-by" className="text-tblue font-medium mb-1">
                            Sort By
                        </label>
                        <select
                            id="sort-by"
                            value={sortBy}
                            onChange={(e) => {
                                setSortBy(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm 
                         focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                        >
                            <option value="created_at">Date Created</option>
                            <option value="name">Name</option>
                            <option value="age">Age</option>
                            {/* Add any other fields you might want to sort by */}
                        </select>
                    </div>

                    {/* Sorting: Order */}
                    <div className="flex flex-col">
                        <label htmlFor="sort-order" className="text-tblue font-medium mb-1">
                            Order
                        </label>
                        <select
                            id="sort-order"
                            value={sortOrder}
                            onChange={(e) => {
                                setSortOrder(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded-lg p-2 bg-white shadow-sm 
                         focus:outline-none focus:ring-1 focus:ring-primary transition duration-200"
                        >
                            <option value="desc">Descending</option>
                            <option value="asc">Ascending</option>
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="breed-filter" className="text-tblue font-medium mb-1 ">
                            CSV
                        </label>


                        <button onClick={handleDownloadCSV} className="border p-2 rounded-lg bg-green-600 text-white">
                            <FontAwesomeIcon icon={faDownload} className="mr-2" />
                            Download
                        </button>
                    </div>
                </div>
            </div>

            {/* Header and Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-4 sm:space-y-0">
                <h1 className="text-tblue text-2xl sm:text-3xl font-bold">Pets</h1>

                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                    {showAdvanced && (
                        <button
                            onClick={handleResetFilters}
                            className="px-3 py-2 text-sm font-medium bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600 transition-all duration-300 w-full sm:w-auto"
                        >
                            <FontAwesomeIcon icon={faRefresh} className="mr-2" />
                            Reset Filters
                        </button>
                    )}

                    <button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="bg-tblue text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 shadow-md hover:bg-[#2c257e] transition-all duration-300 w-full sm:w-auto"
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        Filters
                    </button>

                    <button
                        onClick={handleAdd}
                        className="bg-primary text-white px-4 py-2 rounded-lg flex items-center justify-center hover:bg-accent w-full sm:w-auto"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add New Pet
                    </button>
                </div>
            </div>

            {/* Search Section */}
            <div className="mb-6">
                <div className="flex items-center border border-gray-300 rounded-lg p-2 transition-all duration-300 hover:shadow-lg focus-within:shadow-2xl focus-within:bg-bodyMain">
                    <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2" />
                    <input
                        type="text"
                        placeholder="Search by Name, Owner..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="outline-none bg-transparent w-full text-sm sm:text-base text-gray-700 placeholder-gray-400"
                    />
                </div>
            </div>

            {/* Table Section with Horizontal Scroll */}
            <div className="overflow-x-auto relative rounded-lg">
                <table className="min-w-full bg-white rounded-lg shadow-md">
                    <thead className="bg-primary text-white">
                        <tr>
                            <th className="py-3 px-4 text-left sticky left-0 bg-primary z-10 border-r border-white">Name</th>
                            <th className="py-3 px-4 text-left">Age</th>
                            <th className="py-3 px-4 text-left">Weight</th>
                            <th className="py-3 px-4 text-left">Is Vaccinated</th>
                            <th className="hidden sm:table-cell py-3 px-4 text-left">Breed</th>
                            <th className="hidden sm:table-cell py-3 px-4 text-left">Type</th>
                            <th className="hidden sm:table-cell py-3 px-4 text-left">Allergies</th>
                            <th className="py-3 px-4 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pets.length > 0 ? (
                            pets.map((pet) => (
                                <tr key={pet.id} className="border-b hover:bg-gray-50">
                                    <td className="py-2 px-4 font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white z-10 border-r border-gray-200">
                                        {pet.name || 'N/A'}
                                    </td>
                                    <td className="py-2 px-4 text-gray-600">
                                        {pet.age ? `${pet.age} years` : 'Unknown'}
                                    </td>
                                    <td className="py-2 px-4 text-gray-600">
                                        {pet.weight ? `${pet.weight} kg` : 'N/A'}
                                    </td>
                                    <td className="py-2 px-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${pet.is_vaccinated
                                            ? 'bg-green-200 text-green-800'
                                            : 'bg-red-200 text-red-800'
                                            }`}>
                                            {pet.is_vaccinated ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td className="hidden sm:table-cell py-2 px-4 text-gray-600">
                                        {pet.breed?.name || 'Unknown Breed'}
                                    </td>
                                    <td className="hidden sm:table-cell py-2 px-4 text-gray-600">
                                        {pet.type?.name || 'Unknown Type'}
                                    </td>
                                    <td className="hidden sm:table-cell py-2 px-4">
                                        {pet.allergies && pet.allergies.length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {pet.allergies.map((allergy) => (
                                                    <span
                                                        key={allergy.id}
                                                        className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full"
                                                    >
                                                        {allergy.name}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-500">No Allergies</span>
                                        )}
                                    </td>
                                    <td className="py-2 px-4 text-center">
                                        <Menu as="div" className="relative inline-block text-left">
                                            <Menu.Button className="text-gray-500 hover:text-gray-700 cursor-pointer">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="2"
                                                    stroke="currentColor"
                                                    className="w-6 h-6"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 6.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 18.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                                                    />
                                                </svg>
                                            </Menu.Button>

                                            <Menu.Items className="absolute right-5 -top-10 w-40 bg-bodyMain shadow-lg rounded-lg border border-accent focus:outline-none">
                                                <div className="py-1">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <button
                                                                onClick={() => handleEdit(pet)}
                                                                className={`${active ? 'bg-purple-600 text-white' : 'text-gray-700'
                                                                    } group flex rounded-md items-center justify-center w-full px-4 py-2 text-sm`}
                                                            >
                                                                Edit
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                    {/* <Menu.Item>
                                                        {({ active }) => (
                                                            <button
                                                                onClick={() => handleDelete(pet.id)}
                                                                className={`${active ? 'bg-purple-600 text-white' : 'text-gray-700'
                                                                } group flex rounded-md items-center justify-center w-full px-4 py-2 text-sm`}
                                                            >
                                                                Delete
                                                            </button>
                                                        )}
                                                    </Menu.Item> */}
                                                </div>
                                            </Menu.Items>
                                        </Menu>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="py-8 text-center text-gray-500">
                                    No pets available.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Smart Pagination */}
            <Pagination
                meta={meta}
                onPageChange={(page) => setCurrentPage(page)}
            />

            {/* CRU Modal */}
            {isModalOpen && (
                <CRUModal
                    item={editPet}
                    characteristics={characteristics}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    fetchCharacteristics={fetchCharacteristics}
                />
            )}
        </div>
    );
};


export default AdminPet;

export const CRUModal = ({ item, characteristics, fetchCharacteristics, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        name: '',
        age: '',
        weight: '',
        pet_type_id: '',
        pet_breed_id: '',
        owner_id: '',
        gender: 'unknown',
        microchip_number: '',
        is_active: true,
        selectedAllergies: [],
        images: [],
        removedImages: [],
        vaccine_certificate: null,
        removedVaccineCertificate: false,
    });

    const [selectedPetType, setSelectedPetType] = useState(null);
    const [selectedPetBreed, setSelectedPetBreed] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]); // To track newly selected images
    const [previewImages, setPreviewImages] = useState([]); // Preview for new images
    const [vaccinePreview, setVaccinePreview] = useState(null);
    const [selectedOwner, setSelectedOwner] = useState(null);


    // Only fetch characteristics when the item changes, and ensure it's not called repeatedly
    useEffect(() => {
        if (item) {
            // Populate form data
            setFormData({
                _method: 'put',
                name: item.name || '',
                age: item.age || '',
                weight: item.weight || '',
                pet_type_id: item.type?.id || '',
                pet_type_name: item.type?.name || '',
                pet_breed_id: item.breed?.id || '',
                pet_breed_name: item.breed?.name || '',
                gender: item.gender || 'unknown',
                microchip_number: item.microchip_number || '',
                selectedAllergies: item.allergies ? item.allergies.map((allergy) => allergy.id) : [],
                images: item.images || [],
                removedImages: [],
                owner_id: item.owner?.id || '',
                vaccine_certificate: item.vaccine_certificate || null,
                removedVaccineCertificate: false,
            });

            // Set the selected pet type and breed from the response data
            // const initialType = characteristics.pet_types.find((type) => type.id === item.type?.id);
            // const initialBreed = characteristics.pet_breeds.find((breed) => breed.id === item.breed?.id) ;

            // setSelectedPetType(initialType);
            // setSelectedPetBreed(initialBreed);


            const initialOwner = characteristics.owners.find((owner) => owner.id === item.owner?.id) || null;
            setSelectedOwner(initialOwner);

            // Only fetch characteristics once when the item type exists
            if (item.type) {
                fetchCharacteristics(item.type.id);
            }
            if (item.vaccine_certificate) {
                setVaccinePreview(item.vaccine_certificate);
            }



        }
    }, []);


    const handleOwnerChange = (value) => {
        setSelectedOwner(value);
        setFormData({ ...formData, owner_id: value?.id || '' });
    };


    const handleVaccineCertificateChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prevData) => ({
                ...prevData,
                vaccine_certificate: file,
                removedVaccineCertificate: false,
            }));
            setVaccinePreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveVaccineCertificate = () => {
        setFormData({ ...formData, vaccine_certificate: null, removedVaccineCertificate: true });
        setVaccinePreview(null);
    };

    // Handle selecting new images
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles((prev) => [...prev, ...files]);

        // Preview the newly selected images
        const previews = files.map((file) => URL.createObjectURL(file));
        setPreviewImages((prev) => [...prev, ...previews]);
    };

    // Remove existing image (edit mode)
    const handleRemoveExistingImage = (imageId) => {
        setFormData({
            ...formData,
            removedImages: [...formData.removedImages, imageId], // Track removed images
            images: formData.images.filter((img) => img.id !== imageId) // Remove from UI
        });
    };

    // Remove newly added (preview) images
    const handleRemoveNewImage = (index) => {
        setPreviewImages(previewImages.filter((_, i) => i !== index));
        setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    };

    // Synchronize formData when type, breed, or owner changes
    const handleTypeChange = (value) => {
        setSelectedPetType(value);
        setFormData({ ...formData, pet_type_id: value?.id || '' });
        fetchCharacteristics(value?.id); // Fetch breeds/allergies for selected type
    };

    const handleBreedChange = (value) => {
        setSelectedPetBreed(value);
        setFormData({ ...formData, pet_breed_id: value?.id || '' });
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleAllergyChange = (allergyId) => {
        setFormData((prevData) => {
            const isSelected = prevData.selectedAllergies.includes(allergyId);
            const updatedAllergies = isSelected
                ? prevData.selectedAllergies.filter((id) => id !== allergyId)
                : [...prevData.selectedAllergies, allergyId];
            return { ...prevData, selectedAllergies: updatedAllergies };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const { selectedAllergies, removedImages, removedVaccineCertificate, vaccine_certificate, ...petData } = formData;

            const formDataToSend = new FormData();
            Object.keys(petData).forEach((key) => {
                formDataToSend.append(key, petData[key]);
            });

            // Handle allergies - explicitly send empty array if no allergies selected
            if (selectedAllergies.length === 0) {
                formDataToSend.append('allergies[]', ''); // Send empty value to indicate no allergies
                formDataToSend.append('clear_allergies', true); // Flag to indicate allergies should be cleared
            } else {
                selectedAllergies.forEach((id) => {
                    formDataToSend.append('allergies[]', id);
                });
            }

            if (selectedFiles.length === 0) {
                formDataToSend.append('images', []);
            } else {
                selectedFiles.forEach((file) => {
                    formDataToSend.append('images[]', file);
                });
            }

            removedImages.forEach((imageId) => {
                formDataToSend.append('removed_images[]', imageId); // Attach removed image IDs
            });

            if (vaccine_certificate instanceof File) {
                formDataToSend.append('vaccine_certificate', vaccine_certificate);
            }

            if (removedVaccineCertificate) {
                formDataToSend.append('removed_vaccine_certificate', true);
            }

            if (item) {
                await api.post(`/pets/${item.id}`, formDataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Pet updated successfully');
            } else {
                await api.post('/pets', formDataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Pet created successfully');
            }
            onSave();
        } catch (error) {
            toast.error('Error saving pet. Please check the form.');
        }
    };

    // Filter function for pet types, breeds, and owners based on query input
    // const filterPetTypes = (query) => {
    //     return characteristics.pet_types.filter((type) =>
    //         type.name.toLowerCase().includes(query.toLowerCase())
    //     );
    // };

    // const filterPetBreeds = (query) => {
    //     return characteristics.pet_breeds.filter((breed) =>
    //         breed.name.toLowerCase().includes(query.toLowerCase())
    //     );
    // };


    return (
        <Transition appear show={true} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onCancel}>
                <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />

                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white  text-left align-middle shadow-xl transition-all">

                                <div className="bg-tblue text-white p-4">
                                    <h1 className="text-2xl font-bold"> {item ? 'Edit Pet' : 'Add New Pet'}</h1>
                                </div>

                                <div className="mt-4 p-6">
                                    <form onSubmit={handleSubmit}>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                                            {/* Pet Name */}
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name || ''}
                                                    onChange={handleChange}
                                                    required
                                                    className="block w-full p-2 border rounded-lg"
                                                />
                                            </div>

                                            {/* Age */}
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Age (Years)</label>
                                                <input
                                                    type="number"
                                                    name="age"
                                                    value={formData.age || ''}
                                                    onChange={handleChange}
                                                    className="block w-full p-2 border rounded-lg"
                                                />
                                            </div>

                                            {/* Weight */}
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (KG)</label>
                                                <input
                                                    type="number"
                                                    name="weight"
                                                    step="0.01"
                                                    value={formData.weight || ''}
                                                    onChange={handleChange}
                                                    className="block w-full p-2 border rounded-lg"
                                                />
                                            </div>

                                            {/* Pet Type ComboBox */}
                                            <div className="mb-4">
                                                {
                                                    !item ?
                                                        <ComboBox
                                                            label="Type"
                                                            options={characteristics.pet_types}
                                                            value={selectedPetType}
                                                            onChange={handleTypeChange}
                                                            disabled={!!item}
                                                        /> :
                                                        <>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                                            <input
                                                                type="text"
                                                                value={formData.pet_type_name}
                                                                className="block w-full p-2 border rounded-lg"
                                                                disabled
                                                            />
                                                        </>
                                                }
                                            </div>

                                            {/* Pet Breed ComboBox */}
                                            <div className="mb-4">

                                                {
                                                    !item ?
                                                        <ComboBox
                                                            label="Breed"
                                                            options={characteristics.pet_breeds}
                                                            value={selectedPetBreed}
                                                            onChange={handleBreedChange}
                                                            disabled={!!item}
                                                        />
                                                        :
                                                        <>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
                                                            <input
                                                                type="text"
                                                                value={formData.pet_breed_name}
                                                                className="block w-full p-2 border rounded-lg"
                                                                disabled
                                                            />
                                                        </>
                                                }

                                            </div>


                                            {/* Gender */}
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                                                <select
                                                    name="gender"
                                                    value={formData.gender || 'unknown'}
                                                    onChange={handleChange}
                                                    className="w-full p-2 border rounded-lg"
                                                >
                                                    <option value="unknown">Unknown</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                </select>
                                            </div>

                                            {/* Vaccination Date */}
                                            {/* <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Vaccinated At</label>
                                                <input
                                                    type="date"
                                                    name="vaccinated_at"
                                                    value={formData.vaccinated_at || ''}
                                                    onChange={handleChange}
                                                    className="block w-full p-2 border rounded-lg"
                                                />
                                            </div> */}

                                            {/* Microchip Number */}
                                            {/* <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Microchip Number</label>
                                                <input
                                                    type="text"
                                                    name="microchip_number"
                                                    value={formData.microchip_number || ''}
                                                    onChange={handleChange}
                                                    className="block w-full p-2 border rounded-lg"
                                                />
                                            </div> */}
                                        </div>

                                        {/* owner */}

                                        <div className="mb-4">
                                            <ComboBox
                                                label="Owner"
                                                options={characteristics.owners}
                                                value={selectedOwner}
                                                onChange={handleOwnerChange}
                                                displayKey='email'
                                            />
                                        </div>

                                        {/* Allergies */}
                                        <div className="mb-4">
                                            <label className="block text-lg font-medium text-tblue mb-1">Allergies</label>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {characteristics.pet_allergies.map((allergy) => (
                                                    <div
                                                        key={allergy.id}
                                                        onClick={() => handleAllergyChange(allergy.id)}
                                                        className="flex items-center p-2 bg-white border border-gray-300 rounded-lg hover:shadow-md transition-shadow"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            id={`allergy-${allergy.id}`}
                                                            checked={formData.selectedAllergies.includes(allergy.id)}
                                                            onChange={() => handleAllergyChange(allergy.id)}
                                                            className="form-checkbox h-5 w-5 text-accent rounded focus:ring-accent focus:ring-2"
                                                        />
                                                        <label
                                                            htmlFor={`allergy-${allergy.id}`}
                                                            className={`ml-3 ${formData.selectedAllergies.includes(allergy.id) ? 'text-accent' : 'text-body'}  cursor-pointer hover:text-accent transition-colors`}
                                                        >
                                                            {allergy.name}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>


                                        {/* Image Upload Section */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Images</label>

                                            <div className="flex items-center space-x-4 mt-2">

                                                {/* Show existing images (edit mode) */}
                                                {formData.images.length > 0 && (
                                                    <div className="flex space-x-4">
                                                        {formData.images.map((img) => (
                                                            <div key={img.id} className="relative group">
                                                                <img
                                                                    src={img.url} // Assuming 'url' field in image object
                                                                    alt="Pet image"
                                                                    className="w-24 h-24 object-cover rounded-lg shadow-md transform transition duration-300 ease-in-out hover:scale-105"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                                                                    onClick={() => handleRemoveExistingImage(img.id)}
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Show preview of newly added images */}
                                                {previewImages.length > 0 && (
                                                    <div className="flex space-x-4">
                                                        {previewImages.map((img, index) => (
                                                            <div key={index} className="relative group">
                                                                <img
                                                                    src={img}
                                                                    alt="New upload"
                                                                    className="w-24 h-24 object-cover rounded-lg shadow-md transform transition duration-300 ease-in-out hover:scale-105"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                                                                    onClick={() => handleRemoveNewImage(index)}
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Upload Icon for Adding New Images */}
                                                <div className="relative group">
                                                    <button
                                                        type="button"
                                                        className="w-24 h-24 flex flex-col items-center justify-center border-2 border-purple-500 rounded-lg hover:bg-purple-50 transition-colors duration-300 ease-in-out"
                                                        onClick={() => document.getElementById('file-upload').click()} // Trigger file input click
                                                    >
                                                        <div className="flex items-center justify-center w-10 h-10 bg-purple-500 text-white rounded-full">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-5 w-5"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                            </svg>
                                                        </div>
                                                        <span className="mt-2 text-sm text-gray-500">Add More</span>
                                                    </button>

                                                    {/* Hidden File Input */}
                                                    <input
                                                        id="file-upload"
                                                        type="file"
                                                        multiple
                                                        onChange={handleFileChange} // Your existing handler for file changes
                                                        className="hidden"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Vaccine Certificate Upload */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Vaccine Certificate</label>
                                            <div className="flex items-center space-x-4 mt-2">
                                                {vaccinePreview && (
                                                    <div className="relative group">
                                                        <a href={vaccinePreview} target='_blank'>
                                                            <img
                                                                src={vaccinePreview}
                                                                alt="Vaccine Certificate"
                                                                className="w-24 h-24 object-cover rounded-lg shadow-md transform transition duration-300 hover:scale-105"
                                                            />
                                                        </a>
                                                        <button
                                                            type="button"
                                                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100"
                                                            onClick={handleRemoveVaccineCertificate}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                )}
                                                {!vaccinePreview && (
                                                    <div className="relative group">
                                                        <button
                                                            type="button"
                                                            className="w-24 h-24 flex flex-col items-center justify-center border-2 border-purple-500 rounded-lg hover:bg-purple-50 transition-colors"
                                                            onClick={() => document.getElementById('vaccine-upload').click()}
                                                        >
                                                            <span className="text-gray-500">Upload</span>
                                                        </button>
                                                        <input
                                                            id="vaccine-upload"
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleVaccineCertificateChange}
                                                            className="hidden"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex justify-end mt-6">
                                            <button
                                                type="button"
                                                onClick={onCancel}
                                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg mr-2"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="bg-primary text-white px-4 py-2 rounded-lg"
                                            >
                                                {item ? 'Update' : 'Save'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};




